import React, { useMemo, useState, useCallback, useEffect } from "react";
import axios from "axios";
import { useHistory, Link } from "react-router-dom";

/** ✅ mêmes listes */
const regions = ["Martinique", "Mayotte", "La Réunion", "Guyane Française"];
const europeCountries = [
  "Allemagne", "Autriche", "Belgique", "Bulgarie", "Chypre", "Croatie",
  "Danemark", "Espagne", "Estonie", "Finlande", "France", "Grèce",
  "Hongrie", "Irlande", "Islande", "Italie", "Lettonie", "Lituanie",
  "Luxembourg", "Malte", "Norvège", "Pays-Bas", "Pologne", "Portugal",
  "République Tchèque", "Roumanie", "Royaume-Uni", "Slovaquie", "Slovénie",
  "Suède", "Suisse"
];
const paysOptions = [...regions, ...europeCountries];
const devises = ["EUR", "USD", "GBP", "CHF"];

/** -------- UI helpers -------- */
const StepBadge = React.memo(function StepBadge({ index, currentStep }) {
  const active = currentStep === index;
  const done = currentStep > index;
  return (
    <div className={`st-stepBadge ${active ? "isActive" : ""} ${done ? "isDone" : ""}`}>
      {done ? "✓" : index}
    </div>
  );
});

const Field = React.memo(function Field({ label, error, children }) {
  return (
    <div className="st-field">
      <div className="st-labelRow">
        <label className="st-label">{label}</label>
        {error ? <span className="st-errorTop">{error}</span> : null}
      </div>
      {children}
      {error ? <div className="st-error">{error}</div> : null}
    </div>
  );
});

const UploadCard = React.memo(function UploadCard({
  title,
  hint,
  fieldName,
  file,
  preview,
  error,
  onPick,
  onRemove,
}) {
  return (
    <div className={`st-uploadCard ${error ? "hasError" : ""}`}>
      <div className="st-uploadTop">
        <div>
          <div className="st-uploadTitle">
            <span className="st-uploadIcon" aria-hidden="true">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path d="M12 16V4" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                <path d="M7 9l5-5 5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M20 20H4" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </span>
            {title}
          </div>
          <div className="st-uploadHint">{hint}</div>
        </div>

        {file ? (
          <button type="button" className="st-btn st-btnGhost" onClick={() => onRemove(fieldName)}>
            Supprimer
          </button>
        ) : null}
      </div>

      {!file ? (
        <label className="st-dropzone">
          <input
            type="file"
            accept="image/jpeg,image/png,image/jpg"
            onChange={(e) => onPick(e, fieldName)}
            style={{ display: "none" }}
          />
          <div className="st-dropIcon">⬆️</div>
          <div className="st-dropText">Cliquez pour importer</div>
          <div className="st-dropSub">JPG / PNG — 10 Mo max</div>
        </label>
      ) : (
        <div className="st-preview">
          <img src={preview || ""} alt={title} />
        </div>
      )}

      {error ? <div className="st-error">{error}</div> : null}
    </div>
  );
});

/** -------- Main component -------- */
export default function MultiStepForm() {
  const history = useHistory();

  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [erreur, setErreur] = useState("");

  // ✅ typeAide initialisé sur "Prêt" (non affiché)
  const [formData, setFormData] = useState({
    nom: "", prenom: "", email: "", dateNaissance: "",
    telephone: "", montantDemande: "", projetDescription: "",
    pays: "", ville: "", devise: "", mdp: "",
    adresse: "", sexe: "",
    typeAide: "Prêt",
    statut: "En attente"
  });

  const [files, setFiles] = useState({
    idcard1: null,
    idcard2: null,
    homeproof1: null,
    homeproof2: null,
  });

  const [previews, setPreviews] = useState({
    idcard1: null,
    idcard2: null,
    homeproof1: null,
    homeproof2: null,
  });

  useEffect(() => {
    return () => {
      Object.values(previews).forEach((u) => u && URL.revokeObjectURL(u));
    };
  }, [previews]);

  const today = new Date();
  const maxDateObj = new Date(today.getFullYear() - 16, today.getMonth(), today.getDate());
  const maxDate = maxDateObj.toISOString().split("T")[0];

  const stepTitles = useMemo(() => ([
    "Informations personnelles",
    "Projet & Documents",
    "Sécurité"
  ]), []);

  const stepSubtitles = useMemo(() => ([
    "Renseignez vos informations de base",
    "Décrivez votre demande et ajoutez vos documents",
    "Finalisez en sécurisant votre compte"
  ]), []);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setFormErrors((prev) => ({ ...prev, [name]: "" }));
  }, []);

  const handleFileChange = useCallback((e, fieldName) => {
    const file = e.target.files?.[0];

    if (file && file.size > 10 * 1024 * 1024) {
      setFormErrors((prev) => ({ ...prev, [fieldName]: "Le fichier ne doit pas dépasser 10 Mo" }));
      return;
    }
    if (file && !["image/jpeg", "image/png", "image/jpg"].includes(file.type)) {
      setFormErrors((prev) => ({ ...prev, [fieldName]: "Seuls JPG et PNG sont acceptés" }));
      return;
    }

    if (file) {
      setPreviews((prev) => {
        if (prev[fieldName]) URL.revokeObjectURL(prev[fieldName]);
        return prev;
      });

      const previewUrl = URL.createObjectURL(file);
      setFiles((prev) => ({ ...prev, [fieldName]: file }));
      setPreviews((prev) => ({ ...prev, [fieldName]: previewUrl }));
      setFormErrors((prev) => ({ ...prev, [fieldName]: "" }));
    }
  }, []);

  const handleRemoveFile = useCallback((fieldName) => {
    setPreviews((prev) => {
      if (prev[fieldName]) URL.revokeObjectURL(prev[fieldName]);
      return { ...prev, [fieldName]: null };
    });
    setFiles((prev) => ({ ...prev, [fieldName]: null }));
    setFormErrors((prev) => ({ ...prev, [fieldName]: "" }));
  }, []);

  /** ✅ validations par étape */
  const validateStep1 = useCallback(() => {
    const errors = {};

    if (!formData.nom?.trim()) errors.nom = "Nom obligatoire.";
    if (!formData.prenom?.trim()) errors.prenom = "Prénom obligatoire.";

    if (!formData.dateNaissance) errors.dateNaissance = "Date de naissance obligatoire.";
    else {
      const naissance = new Date(formData.dateNaissance);
      if (Number.isNaN(naissance.getTime()) || naissance > maxDateObj) {
        errors.dateNaissance = "Vous devez avoir au moins 16 ans.";
      }
    }

    if (!formData.sexe) errors.sexe = "Genre obligatoire.";

    if (!formData.email?.trim()) errors.email = "Email obligatoire.";
    else if (!/\S+@\S+\.\S+/.test(formData.email)) errors.email = "Email invalide.";

    if (!formData.telephone?.trim()) errors.telephone = "Téléphone obligatoire.";
    if (!formData.pays) errors.pays = "Pays obligatoire.";
    if (!formData.ville?.trim()) errors.ville = "Ville obligatoire.";
    if (!formData.adresse?.trim()) errors.adresse = "Adresse obligatoire.";

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }, [formData, maxDateObj]);

  const validateStep2 = useCallback(() => {
    const errors = {};

    if (!formData.montantDemande) errors.montantDemande = "Montant obligatoire.";
    else if (Number(formData.montantDemande) <= 0) errors.montantDemande = "Montant invalide.";

    if (!formData.devise) errors.devise = "Devise obligatoire.";
    if (!formData.projetDescription?.trim()) errors.projetDescription = "Description obligatoire.";

    // ✅ au moins 1 pièce d'identité + 1 justificatif
    if (!files.idcard1) errors.idcard1 = "Photo pièce d'identité obligatoire.";
    if (!files.homeproof1) errors.homeproof1 = "Photo justificatif obligatoire.";

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }, [formData, files]);

  const validateStep3 = useCallback(() => {
    const errors = {};
    if (!formData.mdp) errors.mdp = "Mot de passe obligatoire.";
    else if (formData.mdp.length < 6) errors.mdp = "Le mot de passe doit contenir au moins 6 caractères.";
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }, [formData.mdp]);

  const handleNext = () => {
    const ok =
      currentStep === 1 ? validateStep1() :
      currentStep === 2 ? validateStep2() :
      true;

    if (!ok) return;

    if (currentStep < 3) {
      setCurrentStep((p) => p + 1);
      // (optionnel) on peut garder les erreurs vides à chaque étape
      // setFormErrors({});
    }
  };

  const handlePrevious = () => {
    setCurrentStep((p) => p - 1);
    // setFormErrors({});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateStep3()) return;

    setLoading(true);
    setErreur("");

    try {
      const formDataToSend = new FormData();

      Object.entries(formData).forEach(([key, value]) => {
        formDataToSend.append(
          key,
          key === "montantDemande"
            ? parseInt(value || "0", 10).toString()
            : (value ?? "")
        );
      });

      if (files.idcard1) formDataToSend.append("idcard", files.idcard1);
      if (files.idcard2) formDataToSend.append("idcard2", files.idcard2);
      if (files.homeproof1) formDataToSend.append("homeproof", files.homeproof1);
      if (files.homeproof2) formDataToSend.append("homeproof2", files.homeproof2);

      await axios.post("https://appapi.unpaidfinancial.com/api/register", formDataToSend, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      history.push("/login");
    } catch {
      setErreur("Erreur lors de l'enregistrement. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="st-page">
      <style>{css}</style>

      <div className="st-wrap">
        <div className="st-card">
          {/* LEFT BRAND */}
          <aside className="st-brand">
            <div className="st-badge">
              <span aria-hidden="true">📝</span> Formulaire sécurisé
            </div>

            <h1 className="st-brandTitle">{stepTitles[currentStep - 1]}</h1>
            <p className="st-brandText">{stepSubtitles[currentStep - 1]}</p>

            <div className="st-steps">
              {[1, 2, 3].map((s) => (
                <div className="st-step" key={s}>
                  <StepBadge index={s} currentStep={currentStep} />
                  <div className="st-stepMeta">
                    <div className="st-stepTitle">{stepTitles[s - 1]}</div>
                    <div className="st-stepMini">{s === 1 ? "Identité" : s === 2 ? "Projet" : "Compte"}</div>
                  </div>
                  {s < 3 ? <div className={`st-stepLine ${currentStep > s ? "on" : ""}`} /> : null}
                </div>
              ))}
            </div>

            <div className="st-brandNote">
              <div className="st-noteRow">
                <span className="st-noteIcon" aria-hidden="true">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <path d="M12 2l8 4v6c0 5-3.4 9.4-8 10-4.6-.6-8-5-8-10V6l8-4z" stroke="currentColor" strokeWidth="2"/>
                    <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </span>
                <div>
                  <div className="st-noteTitle">Données protégées</div>
                  <div className="st-noteText">Vos informations sont traitées de manière sécurisée.</div>
                </div>
              </div>

              <div className="st-noteRow">
                <span className="st-noteIcon" aria-hidden="true">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <path d="M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" stroke="currentColor" strokeWidth="2"/>
                    <path d="M12 7v5l3 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </span>
                <div>
                  <div className="st-noteTitle">Rapide</div>
                  <div className="st-noteText">3 étapes, validation avant de continuer.</div>
                </div>
              </div>
            </div>
          </aside>

          {/* RIGHT FORM */}
          <section className="st-formSide">
            <div className="st-formHeader">
              <div>
                <h2 className="st-h2">Demande en ligne</h2>
                <p className="st-sub">
                  Tous les champs marqués d’un * sont obligatoires.
                </p>
              </div>

              <button
                type="button"
                className="st-iconBtn"
                onClick={() => history.push("/")}
                title="Retour"
                aria-label="Retour"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>

            <form onSubmit={handleSubmit} className="st-form">
              {/* STEP 1 */}
              {currentStep === 1 && (
                <div className="st-grid st-anim">
                  <Field label="Nom *" error={formErrors.nom}>
                    <input className="st-input" name="nom" value={formData.nom} onChange={handleChange} placeholder="Votre nom" autoComplete="family-name" />
                  </Field>

                  <Field label="Prénom *" error={formErrors.prenom}>
                    <input className="st-input" name="prenom" value={formData.prenom} onChange={handleChange} placeholder="Votre prénom" autoComplete="given-name" />
                  </Field>

                  <Field label="Date de naissance *" error={formErrors.dateNaissance}>
                    <input className="st-input" type="date" name="dateNaissance" max={maxDate} value={formData.dateNaissance} onChange={handleChange} />
                  </Field>

                  <Field label="Genre *" error={formErrors.sexe}>
                    <select className="st-input" name="sexe" value={formData.sexe} onChange={handleChange}>
                      <option value="">Sélectionnez</option>
                      <option value="Masculin">Masculin</option>
                      <option value="Féminin">Féminin</option>
                      <option value="Autre">Autre</option>
                    </select>
                  </Field>

                  <Field label="Email *" error={formErrors.email}>
                    <input className="st-input" type="email" name="email" value={formData.email} onChange={handleChange} placeholder="exemple@domaine.com" autoComplete="email" />
                  </Field>

                  <Field label="Téléphone *" error={formErrors.telephone}>
                    <input className="st-input" type="tel" name="telephone" value={formData.telephone} onChange={handleChange} placeholder="+33 6 00 00 00 00" autoComplete="tel" inputMode="tel" />
                  </Field>

                  <Field label="Pays / Région *" error={formErrors.pays}>
                    <select className="st-input" name="pays" value={formData.pays} onChange={handleChange}>
                      <option value="">Sélectionnez</option>
                      {paysOptions.map((p, idx) => <option key={idx} value={p}>{p}</option>)}
                    </select>
                  </Field>

                  <Field label="Ville *" error={formErrors.ville}>
                    <input className="st-input" name="ville" value={formData.ville} onChange={handleChange} placeholder="Votre ville" autoComplete="address-level2" />
                  </Field>

                  <div className="st-span2">
                    <Field label="Adresse résidentielle *" error={formErrors.adresse}>
                      <input className="st-input" name="adresse" value={formData.adresse} onChange={handleChange} placeholder="Rue, numéro, complément…" autoComplete="street-address" />
                    </Field>
                  </div>
                </div>
              )}

              {/* STEP 2 */}
              {currentStep === 2 && (
                <div className="st-grid st-anim">
                  <Field label="Montant demandé *" error={formErrors.montantDemande}>
                    <input className="st-input" type="number" min="1" name="montantDemande" value={formData.montantDemande} onChange={handleChange} placeholder="Ex: 2500" />
                  </Field>

                  <Field label="Devise *" error={formErrors.devise}>
                    <select className="st-input" name="devise" value={formData.devise} onChange={handleChange}>
                      <option value="">Sélectionnez</option>
                      {devises.map((d, idx) => <option key={idx} value={d}>{d}</option>)}
                    </select>
                  </Field>

                  <div className="st-span2">
                    <Field label="Description du projet *" error={formErrors.projetDescription}>
                      <textarea className="st-input st-textarea" name="projetDescription" value={formData.projetDescription} onChange={handleChange} placeholder="Expliquez brièvement votre demande…" />
                    </Field>
                  </div>

                  <div className="st-span2 st-section">
                    <div className="st-sectionHead">
                      <div>
                        <div className="st-sectionTitle">Pièce d’identité *</div>
                        <div className="st-sectionSub">Ajoutez 1 ou 2 photos</div>
                      </div>
                      <div className="st-chip">Obligatoire</div>
                    </div>

                    <div className="st-uploads">
                      <UploadCard
                        title="Photo 1 (recto)"
                        hint="Obligatoire"
                        fieldName="idcard1"
                        file={files.idcard1}
                        preview={previews.idcard1}
                        error={formErrors.idcard1}
                        onPick={handleFileChange}
                        onRemove={handleRemoveFile}
                      />
                      <UploadCard
                        title="Photo 2 (verso)"
                        hint="Optionnel"
                        fieldName="idcard2"
                        file={files.idcard2}
                        preview={previews.idcard2}
                        error={formErrors.idcard2}
                        onPick={handleFileChange}
                        onRemove={handleRemoveFile}
                      />
                    </div>
                  </div>

                  <div className="st-span2 st-section">
                    <div className="st-sectionHead">
                      <div>
                        <div className="st-sectionTitle">Justificatif de domicile *</div>
                        <div className="st-sectionSub">Ajoutez 1 ou 2 photos</div>
                      </div>
                      <div className="st-chip">Obligatoire</div>
                    </div>

                    <div className="st-uploads">
                      <UploadCard
                        title="Photo 1"
                        hint="Obligatoire"
                        fieldName="homeproof1"
                        file={files.homeproof1}
                        preview={previews.homeproof1}
                        error={formErrors.homeproof1}
                        onPick={handleFileChange}
                        onRemove={handleRemoveFile}
                      />
                      <UploadCard
                        title="Photo 2"
                        hint="Optionnel"
                        fieldName="homeproof2"
                        file={files.homeproof2}
                        preview={previews.homeproof2}
                        error={formErrors.homeproof2}
                        onPick={handleFileChange}
                        onRemove={handleRemoveFile}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* STEP 3 */}
              {currentStep === 3 && (
                <div className="st-grid st-anim">
                  <div className="st-span2">
                    <Field label="Mot de passe *" error={formErrors.mdp}>
                      <div className="st-password">
                        <input
                          className="st-input"
                          type={showPassword ? "text" : "password"}
                          name="mdp"
                          value={formData.mdp}
                          onChange={handleChange}
                          placeholder="Minimum 6 caractères"
                          autoComplete="new-password"
                        />
                        <button
                          type="button"
                          className="st-pinToggle"
                          onClick={() => setShowPassword((v) => !v)}
                          aria-label={showPassword ? "Masquer" : "Afficher"}
                        >
                          {showPassword ? "Masquer" : "Afficher"}
                        </button>
                      </div>
                      <div className="st-help">Minimum 6 caractères.</div>
                    </Field>
                  </div>

                  <div className="st-span2 st-recap">
                    <div className="st-recapTitle">Récapitulatif</div>
                    <div className="st-recapGrid">
                      <div className="st-recapItem"><span>Nom complet</span><strong>{formData.prenom} {formData.nom}</strong></div>
                      <div className="st-recapItem"><span>Email</span><strong>{formData.email}</strong></div>
                      <div className="st-recapItem"><span>Localisation</span><strong>{formData.ville}, {formData.pays}</strong></div>
                      <div className="st-recapItem"><span>Type d’aide</span><strong>{formData.typeAide}</strong></div>
                      <div className="st-recapItem"><span>Montant</span><strong>{formData.montantDemande || "-"} {formData.devise || ""}</strong></div>
                      <div className="st-recapItem"><span>Documents</span><strong>{[files.idcard1, files.idcard2, files.homeproof1, files.homeproof2].filter(Boolean).length} fichier(s)</strong></div>
                    </div>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="st-actions">
                <button
                  type="button"
                  className="st-btn st-btnGhost"
                  onClick={handlePrevious}
                  disabled={currentStep === 1 || loading}
                  style={{ visibility: currentStep === 1 ? "hidden" : "visible" }}
                >
                  ← Précédent
                </button>

                {currentStep < 3 ? (
                  <button type="button" className="st-btn st-btnPrimary" onClick={handleNext} disabled={loading}>
                    Suivant →
                  </button>
                ) : (
                  <button type="submit" className="st-btn st-btnPrimary" disabled={loading}>
                    {loading ? "Envoi..." : "Soumettre ✓"}
                  </button>
                )}
              </div>

              {erreur ? <div className="st-globalError">{erreur}</div> : null}

              <div className="st-foot">
                Vous avez déjà un compte ?{" "}
                <Link className="st-link" to="/login">
                  Se connecter →
                </Link>
              </div>
            </form>
          </section>
        </div>
      </div>
    </div>
  );
}

const css = `
  :root{
    --bg:#0f172a;
    --bg2:#111827;
    --card:#ffffff;
    --text:#0f172a;
    --muted:#64748b;
    --line:rgba(15,23,42,.12);

    --brand:#2563EB;
    --brandHover:#1D4ED8;

    --danger:#e11d48;
    --dangerBg: rgba(225,29,72,.08);

    --soft:#f8fafc;
  }

  *{ box-sizing:border-box; }
  .st-page{
    min-height:100vh;
    padding: 2.25rem 1.25rem;
    background:
      radial-gradient(1200px 600px at 10% -10%, #334155 0%, rgba(30,41,59,0) 60%),
      radial-gradient(1200px 600px at 110% 110%, #0ea5e9 0%, rgba(14,165,233,0) 55%),
      #0f172a;
    font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Arial;
    display:flex;
    align-items:center;
    justify-content:center;
  }

  .st-wrap{ width:100%; max-width: 1080px; }
  .st-card{
    background: var(--card);
    border-radius: 16px;
    overflow: hidden;
    border: 1px solid var(--line);
    box-shadow: 0 30px 60px -15px rgba(2,6,23,0.45);
    display:grid;
    grid-template-columns: 1.05fr .95fr;
  }

  /* LEFT */
  .st-brand{
    background: linear-gradient(135deg, #0f172a, #111827);
    color: #E5E7EB;
    padding: 2.5rem 2.25rem;
    display:flex;
    flex-direction:column;
    gap: 1rem;
    justify-content:center;
    border-right: 1px solid rgba(255,255,255,.10);
  }
  .st-badge{
    width: fit-content;
    display:inline-flex;
    align-items:center;
    gap:.5rem;
    padding:.5rem .75rem;
    border-radius: 999px;
    border: 1px solid rgba(255,255,255,.16);
    background: rgba(255,255,255,.06);
    font-weight: 600;
    font-size: .85rem;
    letter-spacing: .01em;
    color: rgba(229,231,235,.95);
  }
  .st-brandTitle{
    margin: .25rem 0 0;
    font-size: 1.85rem;
    line-height: 1.2;
    font-weight: 700;
    color: #ffffff;
  }
  .st-brandText{
    margin: 0;
    max-width: 420px;
    line-height: 1.6;
    color: rgba(229,231,235,.78);
    font-size: .98rem;
    font-weight: 400;
  }

  .st-steps{
    margin-top: 1rem;
    display:flex;
    flex-direction:column;
    gap: .85rem;
  }
  .st-step{
    display:flex;
    align-items:center;
    gap: .75rem;
  }
  .st-stepMeta{ display:flex; flex-direction:column; gap:2px; min-width: 0; }
  .st-stepTitle{ font-weight: 600; font-size: .95rem; color:#fff; }
  .st-stepMini{ font-size: .85rem; color: rgba(229,231,235,.70); }
  .st-stepLine{
    height: 2px;
    flex:1;
    background: rgba(255,255,255,.14);
    border-radius: 999px;
  }
  .st-stepLine.on{
    background: linear-gradient(90deg, rgba(37,99,235,.95), rgba(14,165,233,.95));
  }

  .st-stepBadge{
    width: 36px; height: 36px;
    border-radius: 12px;
    display:flex; align-items:center; justify-content:center;
    font-weight: 600;
    background: rgba(255,255,255,.08);
    border: 1px solid rgba(255,255,255,.16);
    color: #E5E7EB;
    flex-shrink: 0;
  }
  .st-stepBadge.isActive{
    background: rgba(37,99,235,.18);
    border-color: rgba(37,99,235,.35);
    box-shadow: 0 10px 25px rgba(37,99,235,.18);
  }
  .st-stepBadge.isDone{
    background: rgba(14,165,233,.16);
    border-color: rgba(14,165,233,.30);
  }

  .st-brandNote{
    margin-top: 1.25rem;
    display:grid;
    gap: .9rem;
  }
  .st-noteRow{
    display:flex;
    gap: .75rem;
    align-items:flex-start;
    padding: .9rem;
    border-radius: 14px;
    border: 1px solid rgba(255,255,255,.14);
    background: rgba(255,255,255,.06);
  }
  .st-noteIcon{
    width: 38px; height: 38px;
    border-radius: 12px;
    display:flex; align-items:center; justify-content:center;
    background: rgba(96,165,250,.12);
    border: 1px solid rgba(96,165,250,.22);
    color: #93C5FD;
    flex-shrink: 0;
  }
  .st-noteTitle{ font-weight: 600; color:#fff; }
  .st-noteText{ margin-top: 3px; font-size: .9rem; color: rgba(229,231,235,.78); line-height: 1.45; font-weight:400; }

  /* RIGHT */
  .st-formSide{
    padding: 2.5rem 2.25rem;
    display:flex;
    flex-direction:column;
    justify-content:center;
    gap: 1rem;
  }

  .st-formHeader{
    display:flex;
    align-items:flex-start;
    justify-content:space-between;
    gap: 1rem;
    margin-bottom: .25rem;
  }
  .st-h2{
    margin:0;
    font-size: 1.25rem;
    font-weight: 700;
    color: var(--text);
  }
  .st-sub{
    margin: .35rem 0 0;
    color: var(--muted);
    font-size: .95rem;
    line-height: 1.5;
    font-weight: 400;
  }

  .st-iconBtn{
    width: 42px;
    height: 42px;
    border-radius: 12px;
    border: 1px solid var(--line);
    background: rgba(15,23,42,.04);
    display:inline-flex;
    align-items:center;
    justify-content:center;
    cursor:pointer;
    transition: .2s ease;
    color: var(--text);
  }
  .st-iconBtn:hover{
    background: rgba(15,23,42,.07);
    border-color: rgba(15,23,42,.18);
  }

  .st-form{ display:flex; flex-direction:column; gap: 1rem; margin-top: .5rem; }

  .st-grid{
    display:grid;
    grid-template-columns: 1fr 1fr;
    gap: 14px;
  }
  .st-span2{ grid-column: span 2; }
  .st-anim{ animation: stIn .18s ease both; }
  @keyframes stIn{ from{ opacity:0; transform: translateY(6px);} to{ opacity:1; transform: translateY(0);} }

  .st-field{ display:flex; flex-direction:column; gap: 6px; }
  .st-labelRow{ display:flex; align-items:center; justify-content:space-between; gap: 10px; }
  .st-label{ font-size: .92rem; font-weight: 500; color: rgba(15,23,42,.85); }
  .st-errorTop{ font-size: .85rem; font-weight: 500; color: var(--danger); }
  .st-error{
    margin-top: 6px;
    font-size: .85rem;
    font-weight: 500;
    color: var(--danger);
    background: var(--dangerBg);
    border: 1px solid rgba(225,29,72,.20);
    padding: .55rem .65rem;
    border-radius: 12px;
  }

  .st-input{
    width: 100%;
    padding: .9rem 1rem;
    border-radius: 12px;
    border: 1px solid #e5e7eb;
    background: var(--soft);
    font-size: 1rem;
    color: var(--text);
    outline: none;
    transition: .15s ease;
    font-weight: 400;
  }
  .st-input::placeholder{
    color: rgba(100,116,139,.9);
  }
  .st-input:focus{
    border-color: rgba(37,99,235,.55);
    box-shadow: 0 0 0 4px rgba(37,99,235,.12);
    transform: translateY(-1px);
  }
  select.st-input{ appearance:auto; background: var(--soft); }
  .st-textarea{ min-height: 110px; resize: vertical; }

  .st-section{
    border: 1px solid var(--line);
    background: #ffffff;
    border-radius: 16px;
    padding: 14px;
  }
  .st-sectionHead{
    display:flex;
    align-items:flex-start;
    justify-content:space-between;
    gap: 12px;
    margin-bottom: 12px;
  }
  .st-sectionTitle{ font-weight: 600; color: var(--text); }
  .st-sectionSub{ margin-top: 4px; font-size: .92rem; color: var(--muted); font-weight: 400; }

  .st-chip{
    font-size: .8rem;
    font-weight: 500;
    color: #1D4ED8;
    background: rgba(37,99,235,.10);
    border: 1px solid rgba(37,99,235,.18);
    padding: .35rem .6rem;
    border-radius: 999px;
    white-space: nowrap;
  }

  .st-uploads{
    display:grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
  }

  .st-uploadCard{
    border: 1px solid var(--line);
    border-radius: 16px;
    background: #fff;
    padding: 12px;
    transition: .2s ease;
  }
  .st-uploadCard:hover{
    transform: translateY(-1px);
    border-color: rgba(15,23,42,.18);
  }
  .st-uploadCard.hasError{
    border-color: rgba(225,29,72,.28);
  }

  .st-uploadTop{
    display:flex;
    align-items:flex-start;
    justify-content:space-between;
    gap: 10px;
    margin-bottom: 10px;
  }
  .st-uploadTitle{
    font-weight: 600;
    color: var(--text);
    display:flex;
    align-items:center;
    gap: .5rem;
  }
  .st-uploadIcon{ color: rgba(15,23,42,.55); display:inline-flex; }
  .st-uploadHint{
    margin-top: 2px;
    font-size: .92rem;
    color: var(--muted);
    font-weight: 400;
  }

  .st-dropzone{
    display:flex;
    flex-direction:column;
    align-items:center;
    justify-content:center;
    gap: 6px;
    padding: 18px 12px;
    border-radius: 14px;
    border: 1px dashed rgba(15,23,42,.22);
    cursor:pointer;
    background: rgba(15,23,42,.03);
    transition: .2s ease;
    text-align:center;
  }
  .st-dropzone:hover{
    border-color: rgba(37,99,235,.45);
    box-shadow: 0 0 0 4px rgba(37,99,235,.10);
    transform: translateY(-1px);
  }
  .st-dropText{ font-weight: 600; color: var(--text); }
  .st-dropSub{ font-size: .92rem; color: var(--muted); font-weight:400; }

  .st-preview{
    border-radius: 14px;
    overflow:hidden;
    border: 1px solid var(--line);
    height: 190px;
    background: rgba(15,23,42,.05);
  }
  .st-preview img{
    width: 100%;
    height: 100%;
    object-fit: cover;
    display:block;
  }

  .st-password{ position: relative; }
  .st-pinToggle{
    position:absolute;
    right: 8px;
    top: 50%;
    transform: translateY(-50%);
    border-radius: 10px;
    border: 1px solid var(--line);
    background: rgba(15,23,42,.04);
    padding: .55rem .7rem;
    font-weight: 500;
    cursor: pointer;
    color: var(--text);
    transition:.2s ease;
  }
  .st-pinToggle:hover{
    background: rgba(15,23,42,.07);
    border-color: rgba(15,23,42,.18);
  }
  .st-help{
    margin-top: 6px;
    color: var(--muted);
    font-size: .92rem;
    font-weight: 400;
  }

  .st-recap{
    border-radius: 16px;
    border: 1px solid rgba(37,99,235,.18);
    background: rgba(37,99,235,.06);
    padding: 14px;
  }
  .st-recapTitle{ font-weight: 600; color: var(--text); margin-bottom: 10px; }
  .st-recapGrid{
    display:grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
  }
  .st-recapItem{
    border-radius: 14px;
    border: 1px solid rgba(15,23,42,.10);
    background: rgba(255,255,255,.85);
    padding: 10px;
    display:flex;
    flex-direction:column;
    gap: 4px;
  }
  .st-recapItem span{ font-size: .88rem; color: var(--muted); font-weight:400; }
  .st-recapItem strong{ font-size: .95rem; color: var(--text); font-weight:600; }

  .st-actions{
    display:flex;
    justify-content:space-between;
    gap: 12px;
    margin-top: 6px;
  }
  .st-btn{
    flex:1;
    padding: .95rem 1rem;
    border-radius: 12px;
    font-weight: 600;
    border: 1px solid var(--line);
    cursor:pointer;
    transition: .2s ease;
  }
  .st-btnPrimary{
    background: var(--brand);
    color: #fff;
    border-color: transparent;
  }
  .st-btnPrimary:hover:not(:disabled){
    background: var(--brandHover);
    transform: translateY(-1px);
    box-shadow: 0 14px 26px -18px rgba(0,0,0,0.55);
  }
  .st-btnGhost{
    background: rgba(15,23,42,.04);
    color: var(--text);
  }
  .st-btnGhost:hover:not(:disabled){
    background: rgba(15,23,42,.07);
    transform: translateY(-1px);
  }
  .st-btn:disabled{ opacity: .65; cursor:not-allowed; transform:none; box-shadow:none; }

  .st-globalError{
    margin-top: 10px;
    padding: .85rem .9rem;
    border-radius: 12px;
    border: 1px solid rgba(225,29,72,.22);
    background: var(--dangerBg);
    color: var(--danger);
    font-weight: 600;
    text-align:center;
  }

  .st-foot{
    text-align:center;
    margin-top: .35rem;
    color: var(--muted);
    font-size: .95rem;
    font-weight: 400;
  }
  .st-link{
    color: var(--brand);
    font-weight: 600;
    text-decoration:none;
  }
  .st-link:hover{ text-decoration: underline; }

  @media (max-width: 980px){
    .st-card{ grid-template-columns: 1fr; }
    .st-brand{ border-right: none; border-bottom: 1px solid rgba(255,255,255,.10); }
  }
  @media (max-width: 720px){
    .st-grid{ grid-template-columns: 1fr; }
    .st-span2{ grid-column: auto; }
    .st-uploads{ grid-template-columns: 1fr; }
    .st-recapGrid{ grid-template-columns: 1fr; }
    .st-brandTitle{ font-size: 1.6rem; }
  }
`;
